import { ComponentProps, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanySearchRow } from '../../services/service-company-search';
import CompanySearch from '../app-company-search/CompanySearch';
import {
  CompanySearchLink,
  CompanySearchUnavailable,
} from '../app-company-search/CompanySearchView';
import useCompanySearchAvailability from '../app-company-search/useCompanySearchAvailability';
import OrgNumberField from './OrgNumberField';

type Props = {
  orgNumber: Omit<ComponentProps<typeof OrgNumberField>, 'inputRef' | 'note'>;
  picked: CompanySearchRow | null;
  onCompanyChange: (company: CompanySearchRow | null) => void;
};

export default function OrganizationCompany({ orgNumber, picked, onCompanyChange }: Props) {
  const [t] = useTranslation('organizations', { keyPrefix: 'add' });
  const { down, onUnavailable, typedFieldRef } = useCompanySearchAvailability();
  const [typesOrgNumber, setTypesOrgNumber] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const focusAfterEntrySwitch = useRef(false);

  useEffect(() => {
    if (!focusAfterEntrySwitch.current) return;
    focusAfterEntrySwitch.current = false;
    (typesOrgNumber ? typedFieldRef : searchRef).current?.focus();
  }, [typesOrgNumber]);

  const enterOrgNumberManually = () => {
    focusAfterEntrySwitch.current = true;
    setTypesOrgNumber(true);
  };

  const returnToCompanySearch = () => {
    focusAfterEntrySwitch.current = true;
    setTypesOrgNumber(false);
    orgNumber.onClear();
  };

  return (
    <>
      {down === 'unavailable' && <CompanySearchUnavailable />}
      {typesOrgNumber ? (
        <OrgNumberField
          {...orgNumber}
          inputRef={typedFieldRef}
          note={
            <CompanySearchLink onClick={returnToCompanySearch}>
              {t('button.searchRegister', 'Sök i företagsregistret i stället')}
            </CompanySearchLink>
          }
        />
      ) : down ? (
        <OrgNumberField {...orgNumber} inputRef={typedFieldRef} />
      ) : (
        <CompanySearch
          value={picked}
          onChange={onCompanyChange}
          onUnavailable={onUnavailable}
          label={t('label.company', 'Företag')}
          required
          errorMessage={orgNumber.takenMessage || orgNumber.requiredMessage}
          onEnterManually={enterOrgNumberManually}
          inputRef={searchRef}
        />
      )}
    </>
  );
}
